import { AlertCircle } from "lucide-react";
import { useState, useRef } from "react";
import { toImageSrc } from "@/components/products/ProductForm";
import { getCategoryColor, getCardStyleConfig } from "@/components/products/cardStyleUtils";

export default function ProductCard({ product, onAdd, onLongPress, displaySettings, displayName, tabColor, buttonStyleOverride, buttonBorderOverride, hasSubButtons, buttonImage, subButtonsImage }) {
  const [isPressed, setIsPressed] = useState(false);
  const pressTimer = useRef(null);
  // Split price into dollars and cents
  const priceString = (product.retail_price || product.price || 0).toFixed(2);
  const [dollars, cents] = priceString.split('.');

  // Default settings if not provided
  const settings = displaySettings || {
    show_images: false,
    product_name_font: "Arial",
    product_name_size: 9,
    product_name_bold: false,
    price_font: "Arial",
    price_size: 14,
    price_bold: true,
    price_button_color: "#2563EB",
    card_style: "gradient-border",
    shadow_color: "#9CA3AF",
    shadow_pressed_color: "#3B82F6"
  };

  // Use per-button override if set, otherwise fall back to global setting
  const cardStyle = buttonStyleOverride || settings.card_style || "gradient-border";
  const shadowColor = settings.shadow_color || "#9CA3AF";
  const shadowPressedColor = settings.shadow_pressed_color || "#3B82F6";
  const borderPressedColor = settings.border_pressed_color || "#000000";
  // Per-button border color override
  const buttonBorderColor = buttonBorderOverride || null;
  
  // Check if we should show full image mode (image ON + product has image), or a per-button
  // image was explicitly set (Paste URL / Upload / AI) — a per-button image always overrides
  // the global show_images toggle since the user chose it deliberately for this button.
  const showFullImage = Boolean(buttonImage) || Boolean(subButtonsImage) || (settings.show_images && product.image_url);
  const imageSrc = toImageSrc(buttonImage || subButtonsImage || product.image_url);

  const handleMouseDown = () => {
    setIsPressed(true);
    if (onLongPress) {
      pressTimer.current = setTimeout(() => {
        onLongPress(product);
        setIsPressed(false);
      }, 500); // 500ms long press
    }
  };

  const handleMouseUp = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    if (isPressed) {
      onAdd(product);
    }
    setIsPressed(false);
  };

  const handleMouseLeave = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    setIsPressed(false);
  };

  const handleTouchStart = () => {
    setIsPressed(true);
    if (onLongPress) {
      pressTimer.current = setTimeout(() => {
        onLongPress(product);
        setIsPressed(false);
      }, 500);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    if (isPressed) {
      onAdd(product);
    }
    setIsPressed(false);
  };

  const productDisplayName = displayName || product.display_name || product.name;

  const categoryColor = getCategoryColor(product.category);
  
  // Use per-button border color if set, otherwise use category color
  const effectiveBorderColor = buttonBorderColor || categoryColor;
  const isGradientBorder = effectiveBorderColor?.startsWith("linear-gradient");

  // Shadow using customizable colors from settings
  const buttonShadow = isPressed 
    ? `2px 2px 8px ${shadowPressedColor}99` 
    : `3px 3px 6px ${shadowColor}66`;

  // Border when pressed
  const pressedBorder = isPressed ? `2px solid ${borderPressedColor}` : undefined;

  // Neumorphic shadows (kept for reference; not currently used in card styles)
  // const neumorphicShadow = isPressed
  //   ? 'inset 4px 4px 8px #d1d5db, inset -4px -4px 8px #ffffff'
  //   : '6px 6px 12px #d1d5db, -6px -6px 12px #ffffff';

  const styleConfig = getCardStyleConfig({ cardStyle, isPressed, effectiveBorderColor, isGradientBorder, buttonShadow, pressedBorder });

  // ========== FULL IMAGE MODE ==========
  // When show_images is ON and product has an image, show only image (with card styling)
  if (showFullImage) {
    return (
      <div
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className={`relative rounded-lg cursor-pointer group select-none transition-all duration-150 overflow-hidden h-full ${styleConfig.className}`}
        style={styleConfig.style}
        data-testid={`product-card-${product.id}`}
      >
        {/* Full Image Background */}
        <img
          src={imageSrc}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover rounded-lg"
          style={{ 
            margin: cardStyle === 'gradient-border' ? '3px' : '0',
            width: cardStyle === 'gradient-border' ? 'calc(100% - 6px)' : '100%',
            height: cardStyle === 'gradient-border' ? 'calc(100% - 6px)' : '100%'
          }}
        />
        
        {/* Age Restriction Badge - Top Right */}
        {(product.requires_age_verification || product.age_18_plus || product.age_21_plus) && (
          <div 
            className="absolute top-1 right-1 z-10 text-white text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: categoryColor }}
          >
            {product.age_21_plus ? '21+' : '18+'}
          </div>
        )}

        {/* Sub-button indicator - above image */}
        {hasSubButtons && (
          <div className="absolute top-1 right-1 z-10 w-4 h-4 bg-blue-500 rounded-sm flex items-center justify-center shadow" title="Has sub-items">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="white"><rect x="0" y="0" width="4" height="4" rx="0.5"/><rect x="6" y="0" width="4" height="4" rx="0.5"/><rect x="0" y="6" width="4" height="4" rx="0.5"/><rect x="6" y="6" width="4" height="4" rx="0.5"/></svg>
          </div>
        )}
      </div>
    );
  }

  // ========== NORMAL CARD MODE ==========
  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={`relative flex flex-col rounded-lg cursor-pointer group select-none transition-all duration-150 h-full ${styleConfig.className}`}
      style={styleConfig.style}
      data-testid={`product-card-${product.id}`}
    >
      <div className={`flex flex-col items-center justify-center flex-grow px-3 py-3`}>
        {hasSubButtons && (
          <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-sm flex items-center justify-center" title="Has sub-items">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="white"><rect x="0" y="0" width="4" height="4" rx="0.5"/><rect x="6" y="0" width="4" height="4" rx="0.5"/><rect x="0" y="6" width="4" height="4" rx="0.5"/><rect x="6" y="6" width="4" height="4" rx="0.5"/></svg>
          </div>
        )}
        <h3 
          className={`${styleConfig.textColor} ${styleConfig.textShadow || ''} text-center line-clamp-3 leading-tight`}
          style={{ 
            fontFamily: settings.product_name_font,
            fontSize: `${settings.product_name_size}px`,
            fontWeight: settings.product_name_bold ? 700 : (cardStyle === 'gradient-background' ? 600 : 500)
          }}
        >
          {productDisplayName}
        </h3>
        
        {(product.requires_age_verification || product.age_18_plus || product.age_21_plus) && (
          <div className={`flex items-center gap-0.5 mt-1 ${cardStyle === 'gradient-background' ? 'bg-white/90 px-2 py-0.5 rounded-full' : ''}`}>
            <AlertCircle className="w-2 h-2 text-amber-600" />
            <span className={`text-[8px] text-amber-600 ${cardStyle === 'gradient-background' ? 'font-bold' : ''}`}>
              {product.age_21_plus ? '21+' : '18+'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
